import React, { useState } from 'react';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  Paper,
  Stack
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

/**
 * Inline MRO QM completion acknowledgment form (used in review tab and dialog).
 */
const MroQmCompletionAcknowledgmentForm = ({ workOrder, onSuccess, onCancel, embedded = false }) => {
  const [notes, setNotes] = useState('');
  const [remarks, setRemarks] = useState('');
  const [loadingDecision, setLoadingDecision] = useState('');
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const handleSubmit = async (decision) => {
    try {
      setLoadingDecision(decision);
      setError('');

      const cleanedRemarks = remarks.trim();
      if ((decision === 'REJECT' || decision === 'BACK_TO_ENGINEER') && !cleanedRemarks) {
        setError(decision === 'BACK_TO_ENGINEER'
          ? 'Remarks are required when returning work to MRO engineer'
          : 'Rejection remarks are required');
        setLoadingDecision('');
        return;
      }

      await axios.post(`/api/maintenance/${workOrder._id}/mro-qm/acknowledge-completion`, {
        decision,
        notes: notes.trim() || undefined,
        remarks: cleanedRemarks || undefined
      });

      const message = decision === 'BACK_TO_ENGINEER'
        ? 'Work order returned to MRO engineer'
        : decision === 'REJECT'
          ? 'Completion rejected successfully'
          : 'Completion approved successfully';
      showToast(message, 'success');
      onSuccess?.();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to submit MRO QM decision';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoadingDecision('');
    }
  };

  const Wrapper = embedded ? Paper : Box;

  return (
    <Wrapper variant={embedded ? 'outlined' : undefined} sx={{ p: embedded ? 2 : 0 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        MRO QM Completion Review
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        MRO_ENGINEER has completed the work. Approve to proceed to CAMO QM, reject to record an objection,
        or return to engineer for rework.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        multiline
        rows={4}
        label="Approval Notes (Optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Add any notes or comments about the completion acknowledgment..."
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        multiline
        rows={3}
        label="Remarks (required for Reject / Back to Engineer)"
        value={remarks}
        onChange={(e) => setRemarks(e.target.value)}
        placeholder="Explain rejection or what the engineer must correct..."
        sx={{ mb: 2 }}
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
        {onCancel && (
          <Button onClick={onCancel} disabled={Boolean(loadingDecision)}>
            Cancel
          </Button>
        )}
        <Button
          onClick={() => handleSubmit('ACKNOWLEDGE')}
          variant="contained"
          color="success"
          disabled={Boolean(loadingDecision)}
        >
          {loadingDecision === 'ACKNOWLEDGE' ? 'Submitting...' : 'Approve'}
        </Button>
        <Button
          onClick={() => handleSubmit('REJECT')}
          variant="contained"
          color="error"
          disabled={Boolean(loadingDecision) || !remarks.trim()}
        >
          {loadingDecision === 'REJECT' ? 'Submitting...' : 'Reject'}
        </Button>
        <Button
          onClick={() => handleSubmit('BACK_TO_ENGINEER')}
          variant="outlined"
          color="warning"
          disabled={Boolean(loadingDecision) || !remarks.trim()}
        >
          {loadingDecision === 'BACK_TO_ENGINEER' ? 'Submitting...' : 'Back to Engineer'}
        </Button>
      </Stack>
    </Wrapper>
  );
};

export default MroQmCompletionAcknowledgmentForm;
